import React from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { RichText } from 'prismic-reactjs';
import { withState, compose } from 'recompose';
import pageWithProps from 'shared/hoc/pageWithProps';
import { getAllYears } from 'shared/utils/posts';
import { getDocumentByType, getDocumentsByLangWithEnglish } from 'shared/utils/documents';
import {
  PageTitle,
  Section as BaseSection,
  Flex,
  ThumbnailPost,
  NoResultsMessage,
} from 'shared/components';
import { upToMedia } from 'shared/style/media';

const Section = styled(BaseSection).attrs(() => ({ small: true }))`
  flex-direction: column;
`;

const YearsFilter = styled(Flex)`
  width: 100%;
  padding: 0 0 3rem;
  border-bottom: solid 1px;
  border-color: ${({ theme }) => theme.palette.whiteGrey};
  margin: 0 0 8rem;

  ${upToMedia.mobile` 
    padding: 0 0 0rem;
    margin: 0 0 2rem;
  `}
`;

const YearText = styled.div`
  cursor: pointer;
  padding: 2rem 10rem 2rem 0;
  font-size: ${({ theme }) => theme.constants.subTitleSize}rem;
  color: ${({ theme, selected }) => (selected ? theme.palette.blue : theme.textColor)};
  font-weight: 600;
`;

const YearTab = ({ text, selected, onClick }) => (
  <YearText selected={selected} onClick={() => onClick && onClick()}>
    {text}
  </YearText>
);

const News = ({ title, posts, lang, yearIndex, setYearIndex }) => {
  const years = getAllYears(posts).slice(0, 6);

  const filteredPosts = posts
    .filter(({ data: { date } }) => new Date(date).getFullYear() === years[yearIndex])
    .sort((a, b) => {
      if (!a.data.post_index && !b.data.post_index) {
        return new Date(a.data.date) < new Date(b.data.date) ? 1 : -1;
      }
      if (!a.data.post_index || !b.data.post_index) {
        return a.data.post_index ? -1 : 1;
      }
      if (a.lang === lang && b.lang === lang) {
        return a.data.post_index > b.data.post_index ? 1 : -1;
      }
      if (a.lang !== lang && b.lang === lang) {
        return 1;
      }
      if (a.lang === lang && b.lang !== lang) {
        return -1;
      }
      return RichText.asText(a.data.title) - RichText.asText(b.data.title);
    });

  return (
    <>
      <PageTitle title={title} lessBottomMargin />
      <Section>
        <YearsFilter>
          {years.map((year, index) => (
            <YearTab
              key={index}
              selected={yearIndex === index}
              text={year}
              onClick={() => setYearIndex(index)}
            />
          ))}
        </YearsFilter>

        {isEmpty(filteredPosts) ? (
          <NoResultsMessage />
        ) : (
          filteredPosts.map(({ data }, idx) => <ThumbnailPost key={idx} post={data} />)
        )}
      </Section>
    </>
  );
};

const enhance = compose(
  pageWithProps(async ({ lang, ref }) => ({
    ...(await getDocumentByType('news', lang, ref)),
    posts: await getDocumentsByLangWithEnglish('news_post', lang, ref),
    lang,
  })),
  withState('yearIndex', 'setYearIndex', 0),
);

export default enhance(News);
